import type { ActionReducerMapBuilder, AsyncThunk } from '@reduxjs/toolkit';

/**
 * A value that is populated asynchronously.
 *
 * An AsyncValue decorates a value with it's status.  The values have four key states:
 *
 * 1. empty - The value is not present and loading hasn't started.  All fields are false/null.
 * 2. pending - The value is not present but loading has started.  `isLoading` is true.
 * 3. resolved - The value is present and loading has completed.  `value` is not null.
 * 4. rejected - The value is not present and loading has failed.  `errors` is not empty.
 *
 * It is possible to mix these for more complicated behavior.  For example, the
 * value could be present while loading if data is being refreshed or the
 * previous value could be present with errors.
 *
 * @deprecated - Prefer the `useQuery`/`useMutation` hooks from `./app/ReactQuery/` instead. Do not add new usages.
 */
export type AsyncValue<T> = {
  readonly value: T | null;
  readonly isLoading: boolean;
  readonly errors: string[] | null;
};

function asyncValue<T>(
  value: T | null,
  isLoading: boolean,
  errors: string[] | null,
): AsyncValue<T> {
  return { value, isLoading, errors };
}

/** Creates an empty AsyncValue. */
export function empty(): AsyncValue<never> {
  return asyncValue<never>(null, false, null);
}

/** Creates a pending AsyncValue with no data. */
export function pending(): AsyncValue<never> {
  return asyncValue<never>(null, true, null);
}

/** Creates an AsyncValue with the given data. */
export function resolve<T>(value: T): AsyncValue<T> {
  return asyncValue<T>(value, false, null);
}

/** Creates an errored AsyncValue with no data. */
export function reject(errors: string[]): AsyncValue<never> {
  return asyncValue<never>(null, false, errors);
}

/** Returns a new `AsyncValue` that is pending with the same value */
export function setPending<T>(value: AsyncValue<T>): AsyncValue<T> {
  return asyncValue(value.value, true, null);
}

/**
 * Extracts the values from a tuple of AsyncValues. This supports const tuples
 * through the use of `ReadonlyArray`.
 */
type ValueTypes<AsyncValues extends ReadonlyArray<AsyncValue<unknown>>> = {
  [K in keyof AsyncValues]: AsyncValues[K] extends AsyncValue<infer T> ? T : never;
};

/**
 * Creates an AsyncValue that has the data and state of all the input values.
 *
 * The result will have a value only if all the asyncValues have one.
 * The result will be loading if any of the asyncValues are loading.
 * The result will have all the errors from all the asyncValues.
 *
 * The function works well with const tuples.  You must use `as const` to treat
 * an array of values as a tuple.
 */
export function all<AsyncValues extends ReadonlyArray<AsyncValue<unknown>>>(
  asyncValues: AsyncValues,
): AsyncValue<ValueTypes<AsyncValues>> {
  const values = asyncValues.map(v => v.value);
  const isLoading = asyncValues.some((v) => v.isLoading);
  const errors = asyncValues.flatMap((v) => v.errors ? v.errors : []);

  return {
    value: values.every(v => v !== null) ? values.filter(v => v !== null) as ValueTypes<AsyncValues> : null,
    isLoading,
    errors,
  };
}

/**
 * Flatmaps an async value.  If the first value is present then it will invoke
 * the function, otherwise the initial value is returned.
 */
export function flatMap<T, U>(value: AsyncValue<T>, fn: (v: T) => AsyncValue<U>): AsyncValue<U> {
  if (value.value !== null) {
    return fn(value.value);
  }

  // The value isn't present, so type conversion is safe here.
  return value as AsyncValue<any>;
}

/**
 * Maps an async value, only applying the function if the value is present.
 */
export function map<T, U>(value: AsyncValue<T>, fn: (v: T) => U): AsyncValue<U> {
  if (value.value !== null) {
    return {
      ...value,
      value: fn(value.value),
    };
  }

  // The value isn't present, so type conversion is safe here.
  return value as AsyncValue<any>;
}

/**
 * Connects this value to an async thunk in the context of extra reducer functions.
 * - The pending action will set the value pending.
 * - The fulfilled action will resolve the value.
 * - The rejected action will reject the value.
 */
export function connectToAsyncThunk<S, T, Arg, Config extends Record<string, unknown>>(
  builder: ActionReducerMapBuilder<S>,
  thunk: AsyncThunk<T, Arg, Config>,
  setter: (state: S, v: AsyncValue<T>) => S,
) {
  builder.addCase(thunk.pending, (state) => {
    return setter(state, pending());
  });

  builder.addCase(thunk.fulfilled, (state, action) => {
    return setter(state, resolve(action.payload));
  });

  builder.addCase(thunk.rejected, (state, action) => {
    return setter(state, reject([action.payload as string]));
  });
}

/**
 * Helper for taking some action on an AsyncValue, but only
 * if the value is not null.
 */
export function orNull<T, U>(value: AsyncValue<T>, fn: (t: T) => U): U | null {
  if (value.value === null) {
    return null;
  }
  return fn(value.value);
}
